import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogHeader as AlertHdr,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { useTRPC } from '@/trpc/react'
import { EMPLOYEE_SETTINGS_FIELD_GROUP_LABELS, EMPLOYEE_SETTINGS_FIELD_GROUPS } from '@repo/abac'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Check, ChevronLeft, Info, MousePointerClick } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ALL_RESOURCES, getResourceLabel } from './-common/common'

export const Route = createFileRoute('/dashboard/admin/role_permissions/supervisor')({
  component: RouteComponent,
})

const toFilter = ['Permissions']

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const roleName = 'supervisor' as const

  const filteredResources = ALL_RESOURCES.filter(
    (resource) => !toFilter.includes(resource.resource),
  )

  const [matrix, setMatrix] = useState<Record<string, Record<string, boolean>>>(() => {
    const initial: Record<string, Record<string, boolean>> = {}
    for (const def of filteredResources) {
      initial[def.resource] = {}
      for (const action of def.actions) initial[def.resource][action] = false
    }
    return initial
  })

  type FieldName = (typeof EMPLOYEE_SETTINGS_FIELD_GROUPS)[number]
  type ActionName = 'read' | 'create' | 'update'
  const [employeeSettingsFields, setEmployeeSettingsFields] = useState<
    Record<ActionName, Record<FieldName, boolean>>
  >(() => ({
    read: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
    create: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
    update: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
      FieldName,
      boolean
    >,
  }))

  function toggle(resource: string, action: string): void {
    setMatrix((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: !prev[resource]?.[action],
      },
    }))
  }

  // Client-scoped role access, Step 3: marker stored on the Client permission
  // row's `condition` jsonb column. Inert until Step 9 (abilities.ts) reads it.
  const [restrictToAssignedClients, setRestrictToAssignedClients] = useState(false)

  const { mutateAsync: saveRole, isPending } = useMutation({
    ...api.permissions.saveRolePermissions.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: api.user.getUsers.queryKey() })
        toast.success('Permissions saved')
      },
      onError: (error: unknown) => {
        console.error(error)
        toast.error('Failed to save permissions')
      },
    }),
  })

  const rolePermsQuery = useQuery({
    ...api.permissions.getRolePermissions.queryOptions({ roleName }),
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    const rolePerms = rolePermsQuery.data
    if (!rolePerms) return
    const next: Record<string, Record<string, boolean>> = {}
    for (const def of filteredResources) {
      next[def.resource] = {}
      for (const action of def.actions) next[def.resource][action] = false
    }
    const nextFields: Record<ActionName, Record<FieldName, boolean>> = {
      read: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
      create: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
      update: Object.fromEntries(EMPLOYEE_SETTINGS_FIELD_GROUPS.map((f) => [f, false])) as Record<
        FieldName,
        boolean
      >,
    }
    let nextRestrictToAssignedClients = false
    for (const p of rolePerms) {
      if (!next[p.resource]) next[p.resource] = {}
      next[p.resource][p.action] = p.effect === 'allow'
      if (
        p.resource === 'EmployeeSettings' &&
        (p.action === 'read' || p.action === 'create' || p.action === 'update') &&
        Array.isArray(p.fields)
      ) {
        for (const fld of p.fields) {
          if ((EMPLOYEE_SETTINGS_FIELD_GROUPS as readonly string[]).includes(fld as string)) {
            nextFields[p.action as ActionName][fld as FieldName] = true
          }
        }
      }
      if (p.resource === 'Client' && p.condition?.$assignedClientsOnly === true) {
        nextRestrictToAssignedClients = true
      }
    }
    if (JSON.stringify(matrix) !== JSON.stringify(next)) setMatrix(next)
    if (JSON.stringify(employeeSettingsFields) !== JSON.stringify(nextFields))
      setEmployeeSettingsFields(nextFields)
    if (restrictToAssignedClients !== nextRestrictToAssignedClients)
      setRestrictToAssignedClients(nextRestrictToAssignedClients)
  }, [rolePermsQuery.data])

  function handleSave(): void {
    const permissions = Object.entries(matrix).flatMap(([resource, actions]) =>
      Object.entries(actions)
        .filter(([, enabled]) => enabled)
        .map(([action]) => {
          if (
            resource === 'EmployeeSettings' &&
            (action === 'read' || action === 'create' || action === 'update')
          ) {
            const selected = Object.entries(employeeSettingsFields[action as ActionName])
              .filter(([, on]) => on)
              .map(([f]) => f)
            return selected.length > 0
              ? ({ resource, action, effect: 'allow' as const, fields: selected } as const)
              : ({ resource, action, effect: 'allow' as const } as const)
          }
          if (resource === 'Client') {
            return {
              resource,
              action,
              effect: 'allow' as const,
              condition: restrictToAssignedClients ? { $assignedClientsOnly: true } : {},
            }
          }
          return { resource, action, effect: 'allow' as const }
        }),
    )

    void saveRole({ roleName, permissions })
  }

  return (
    <DashboardShell>
      <div className="mb-2 flex items-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/dashboard/admin/role_permissions' })}
        >
          <ChevronLeft className="mr-1" />
          Back
        </Button>
      </div>
      <DashboardHeader heading="Role Permissions" text="Configure role permissions" />
      <Separator />
      <div className="text-sm text-muted-foreground mt-3">
        <span className="font-medium">Note:</span> Enabling &quot;manage&quot; grants read, create,
        update, and delete for that specific resource.
      </div>

      <div className="mt-4 space-y-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 p-3">
        <div className="flex items-center gap-1.5">
          <div className="font-semibold">Data Scope</div>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 text-muted-foreground hover:text-foreground"
              >
                <Info className="h-4 w-4" />
                <span className="sr-only">How does Data Scope work?</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>How Data Scope works</DialogTitle>
                <DialogDescription>
                  Restrict what supervisors can see, based on which clients they&apos;re assigned
                  to.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 text-sm">
                <div className="space-y-2">
                  <div className="font-medium">1. Enable Client: read, then save</div>
                  <p className="text-muted-foreground">
                    The toggle below is stored on the <span className="font-medium">Client</span>{' '}
                    permission, so it needs at least one Client action enabled first. Turn on{' '}
                    <span className="font-medium">read</span> in the{' '}
                    <span className="font-medium">Client</span> section further down this page,
                    then click <span className="font-medium">Save</span> at the bottom and confirm.
                  </p>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 shrink-0 text-blue-500" />
                    <div className="w-full space-y-2 rounded-md border p-2">
                      <div className="font-semibold">Client</div>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                        <div className="ring-primary flex items-center justify-between rounded-md border p-2 ring-2">
                          <Label className="text-sm">read</Label>
                          <div className="flex items-center gap-1">
                            <Switch checked />
                            <MousePointerClick className="text-primary h-3.5 w-3.5" />
                          </div>
                        </div>
                        <div className="flex items-center justify-between rounded-md border p-2">
                          <Label className="text-sm text-muted-foreground">create</Label>
                          <Switch />
                        </div>
                        <div className="flex items-center justify-between rounded-md border p-2">
                          <Label className="text-sm text-muted-foreground">update</Label>
                          <Switch />
                        </div>
                        <div className="flex items-center justify-between rounded-md border p-2">
                          <Label className="text-sm text-muted-foreground">delete</Label>
                          <Switch />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-medium">2. Turn on the toggle below, then save again</div>
                  <p className="text-muted-foreground">
                    When &quot;Restrict to assigned clients only&quot; is on, every user with the{' '}
                    <span className="font-medium">supervisor</span> role can only see clients
                    assigned to them, plus those clients&apos; employees. A scoped supervisor with
                    no clients assigned will see nothing.
                  </p>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 shrink-0 text-blue-500" />
                    <div className="flex flex-1 items-center justify-between rounded-md border bg-background p-2">
                      <Label className="text-sm">Restrict to assigned clients only</Label>
                      <Switch checked />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-medium">3. Enable the role for a user</div>
                  <p className="text-muted-foreground">
                    Go to <span className="font-medium">Users</span>, find the user, click{' '}
                    <span className="font-medium">Configure</span>, and check{' '}
                    <span className="font-medium">Supervisor</span>.
                  </p>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 shrink-0 text-blue-500" />
                    <div className="w-fit space-y-1 rounded-md border bg-background p-2">
                      <Button type="button" tabIndex={-1}>
                        Configure
                      </Button>
                      <div className="bg-popover text-popover-foreground w-56 rounded-md border p-1 shadow-md">
                        <div className="px-2 py-1.5 text-sm font-medium">Select Roles</div>
                        <div className="bg-border -mx-1 my-1 h-px" />
                        <div className="relative flex items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm">
                          <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center" />
                          Admin
                        </div>
                        <div className="ring-primary relative flex items-center gap-2 rounded-sm bg-primary/10 py-1.5 pr-2 pl-8 text-sm ring-2">
                          <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                            <Check className="size-4" />
                          </span>
                          Supervisor
                          <MousePointerClick className="text-primary ml-auto h-3.5 w-3.5" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="font-medium">4. Assign that user&apos;s clients</div>
                  <p className="text-muted-foreground">
                    An <span className="font-medium">Assigned Clients</span> button appears next to
                    Configure once Supervisor is checked. Open it, check the clients this user
                    should have access to, then click the <span className="font-medium">Save</span>{' '}
                    button beside it.
                  </p>
                  <div className="flex flex-wrap items-start gap-2 rounded-md border bg-background p-2">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 shrink-0 text-blue-500" />
                      <div className="w-fit space-y-1">
                        <Button type="button" variant="outline" tabIndex={-1}>
                          Assigned Clients (1)
                        </Button>
                        <div className="bg-popover text-popover-foreground w-56 rounded-md border p-1 shadow-md">
                          <div className="relative flex items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm">
                            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
                              <Check className="size-4" />
                            </span>
                            Acme Corp
                          </div>
                          <div className="relative flex items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm">
                            <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center" />
                            Globex Inc
                          </div>
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      tabIndex={-1}
                      className="ring-primary self-start ring-2"
                    >
                      Save
                      <MousePointerClick className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <p className="text-muted-foreground text-xs italic">
                  Assignments are per user, not per role &mdash; repeat step 4 for each supervisor.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/dashboard/admin/users' })}
                >
                  Go to Users
                </Button>
                <DialogClose asChild>
                  <Button type="button">Got it</Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="text-sm text-muted-foreground">
          This is a role-wide setting, not a per-resource permission. When enabled, this role can
          only see clients assigned to a given user (set per user under Users &rarr; Configure),
          plus those clients&apos; employees. Timesheets, payroll, and other resources will be
          added to this scope in a later phase.
        </div>
        <div className="flex items-center justify-between rounded-md border p-2 bg-background">
          <Label htmlFor={`${roleName}-restrict-assigned-clients`} className="text-sm">
            Restrict to assigned clients only
          </Label>
          <Switch
            id={`${roleName}-restrict-assigned-clients`}
            checked={restrictToAssignedClients}
            onCheckedChange={() => setRestrictToAssignedClients((prev) => !prev)}
            disabled={isPending || rolePermsQuery.isPending}
          />
        </div>
      </div>

      <div className="space-y-4 mt-4">
        <div className="text-3xl font-semibold">supervisor</div>
        {filteredResources.map((def) => (
          <div key={def.resource} className="space-y-2 border p-2 rounded-md">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{getResourceLabel(def.resource)}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {def.actions.map((action) => {
                const checked = !!matrix?.[def.resource]?.[action]
                const id = `${roleName}-${def.resource}-${action}`
                return (
                  <div
                    key={id}
                    className="flex items-center justify-between rounded-md border p-2"
                  >
                    <Label htmlFor={id} className="text-sm">
                      {action}
                    </Label>
                    <Switch
                      id={id}
                      checked={checked}
                      onCheckedChange={() => toggle(def.resource, action)}
                      disabled={isPending || rolePermsQuery.isPending}
                    />
                    {def.resource === 'EmployeeSettings' &&
                      (action === 'read' || action === 'create' || action === 'update') && (
                        <div className="col-span-2 mt-2 grid grid-cols-2 gap-2">
                          {EMPLOYEE_SETTINGS_FIELD_GROUPS.map((fld) => {
                            const fldId = `${id}-${fld}`
                            const on = employeeSettingsFields[action as ActionName][fld]
                            const disableFields = !checked || !!matrix?.EmployeeSettings?.manage
                            return (
                              <div
                                key={fldId}
                                className="flex items-center justify-between rounded-md border p-2"
                              >
                                <Label htmlFor={fldId} className="text-xs">
                                  {EMPLOYEE_SETTINGS_FIELD_GROUP_LABELS[fld]}
                                </Label>
                                <Switch
                                  id={fldId}
                                  checked={on}
                                  onCheckedChange={() =>
                                    setEmployeeSettingsFields((prev) => ({
                                      ...prev,
                                      [action as ActionName]: {
                                        ...prev[action as ActionName],
                                        [fld]: !on,
                                      },
                                    }))
                                  }
                                  disabled={isPending || rolePermsQuery.isPending || disableFields}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button disabled={isPending || rolePermsQuery.isPending} className="w-fit mt-4">
            Save
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertHdr>
            <AlertDialogTitle>Confirm Save</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save these permission changes for the supervisor role? This
              may affect user access across the system.
            </AlertDialogDescription>
          </AlertHdr>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave} disabled={isPending}>
              Save Permissions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  )
}
